import { IBaseModelWoId } from '../base/base-model.interface';
import { createEnumOptions } from '../shared/enum-option.interface';

export interface AllowedProfiles {
  service?: string[];
  line?: string[];
  vas?: string[];
  traffic_up?: string[];
  traffic_down?: string[];
}

export interface IOntUni {
  ontportid: number;
  ontporttype: OntPortType;
  vlandefault?: number;
}

export interface IServicePortSettings {
  lineProfile?: string;
  serviceProfile?: string;
  note?: string;
  servicePorts?: ServicePort[];
  specs: {
    default: boolean;
    code: string;
    servicePorts?: ServicePort[];
    uni?: IOntUni[];
  }[];
}

export interface IOntSettings {
  name: string;
  code: string;
  vasProfile?: string;
  vendorId?: string;
  tierL2?: boolean;
  xgsPon?: boolean;

  data: {
    splitter?: string;
    splitterPort?: number;
    capabprof?: string;
    almprof?: string;
    vlan?: string;
    pri?: number;
    snmpprof?: string;
    optalmprof?: string;
    buildtopo?: string;
    maclearn?: string;
    srvlevelprof?: string;
    psprofile?: string;
    ipgetmode?: IpGetMode;
    upcospolicy?: string;
    downcospolicy?: string;
    bindwidthtype?: BindWidthType;
    managemode?: ManageMode;
    accessscene?: AccessScene;
  };

  servicePortsSettings: IServicePortSettings[];

  igmpVlan?: string;
  igmpVersion?: IgmpVersion;
  maxProgram?: string;
  globalLeave?: GlobalLeave;
}

export interface IProvisioningSettings
  extends IBaseModelWoId<'ProvisioningSettings'> {
  allowedProfiles: AllowedProfiles;
  ontSettings: IOntSettings[];
  trafficProfileUp?: string;
  trafficProfileDown?: string;
  // names of globally configured speed profiles flagged as xgsPon
  xgsPonProfiles?: { up: string[]; down: string[] };
}

export enum ServicePortTagTransform {
  default = '0',
  transparent = '1', // vlanid == uv
  translate = '2',
  translateAndAdd = '3',
}

export const servicePortTagTransportOptions =
  createEnumOptions<ServicePortTagTransform>({
    [ServicePortTagTransform.default]: {
      label: 'Default',
    },
    [ServicePortTagTransform.transparent]: {
      label: 'Transparent',
    },
    [ServicePortTagTransform.translate]: {
      label: 'Translate',
    },
    [ServicePortTagTransform.translateAndAdd]: {
      label: 'Translate and Add',
    },
    _default: {
      label: 'None',
    },
  });

export enum ServicePortType {
  INET = 'inet',
  MCAST = 'mcast',
  MGNT = 'mgnt',
  VOIP = 'voip',
}

export const servicePortTypeOptions = createEnumOptions({
  [ServicePortType.INET]: { label: 'Internet' },
  [ServicePortType.MCAST]: { label: 'Multicast' },
  [ServicePortType.MGNT]: { label: 'Management' },
  [ServicePortType.VOIP]: { label: 'VOIP' },
  _default: { label: '-' },
});

export enum EnableDisable {
  ENABLE = 'Enable',
  DISABLE = 'Disable',
}

export const enableDisableOptions = createEnumOptions({
  [EnableDisable.ENABLE]: { label: 'Enable' },
  [EnableDisable.DISABLE]: { label: 'Disable' },
  _default: { label: '-' },
});

export enum BindWidthType {
  '2.5G-1.25G' = '2.5G-1.25G',
  '10G-2.5G' = '10G-2.5G',
  '10G-10G' = '10G-10G',
  AUTO = 'AUTO',
}

export const bindWidthTypeOptions = createEnumOptions({
  [BindWidthType['2.5G-1.25G']]: { label: '2.5G-1.25G' },
  [BindWidthType['10G-2.5G']]: { label: '10G-2.5G' },
  [BindWidthType['10G-10G']]: { label: '10G-10G' },
  [BindWidthType.AUTO]: { label: 'AUTO' },
  _default: { label: '-' },
});

export enum ManageMode {
  OMCI = 'OMCI',
  SNMP = 'SNMP',
}

export const manageModeOptions = createEnumOptions({
  [ManageMode.OMCI]: { label: 'OMCI' },
  [ManageMode.SNMP]: { label: 'SNMP' },
  _default: { label: '-' },
});

export enum AccessScene {
  P2MP = 'P2MP',
  COMMON = 'COMMON',
  AUTO = 'AUTO',
}

export const accessSceneOptions = createEnumOptions({
  [AccessScene.P2MP]: { label: 'P2MP' },
  [AccessScene.COMMON]: { label: 'COMMON' },
  [AccessScene.AUTO]: { label: 'AUTO' },
  _default: { label: '-' },
});

export enum PortMode {
  PTM = 'ptm',
  ATM = 'atm',
}

export const portModeOptions = createEnumOptions({
  [PortMode.PTM]: { label: 'ptm' },
  [PortMode.ATM]: { label: 'atm' },
  _default: { label: '-' },
});

export enum IpGetMode {
  EMSALLOT = 'EMSALLOT',
}

export const ipGetModeOptions = createEnumOptions({
  [IpGetMode.EMSALLOT]: { label: 'EMSALLOT' },
  _default: { label: '-' },
});

export enum UnknownMulticastPolicy {
  DISCARD = 'discard',
  TRANSPARENT = 'transparent',
}

export const unknownMulticastPolicyOptions = createEnumOptions({
  [UnknownMulticastPolicy.DISCARD]: { label: 'discard' },
  [UnknownMulticastPolicy.TRANSPARENT]: { label: 'transparent' },
  _default: { label: '-' },
});

export enum IgmpPacketPolicy {
  DISCARD = 'discard',
  TRANSPARENT = 'transparent',
  NORMAL = 'normal',
}

export const igmpPacketPolicyOptions = createEnumOptions({
  [IgmpPacketPolicy.DISCARD]: { label: 'discard' },
  [IgmpPacketPolicy.TRANSPARENT]: { label: 'transparent' },
  [IgmpPacketPolicy.NORMAL]: { label: 'normal' },
  _default: { label: '-' },
});

export enum OntPortType {
  E1 = 'E1',
  FE = 'FE',
  GE = 'GE',
  MOCA = 'MOCA',
  CATV = 'CATV',
  IPHOST = 'IPHOST',
  ETH = 'ETH',
  ETHBUNDLE = 'ETHBUNDLE',
  VDSL2 = 'VDSL2',
  POTS = 'POTS',
  WIFI = 'WIFI',
}

export const ontPortTypeOptions = createEnumOptions({
  [OntPortType.E1]: { label: 'E1' },
  [OntPortType.FE]: { label: 'FE' },
  [OntPortType.GE]: { label: 'GE' },
  [OntPortType.MOCA]: { label: 'MOCA' },
  [OntPortType.CATV]: { label: 'CATV' },
  [OntPortType.IPHOST]: { label: 'IPHOST' },
  [OntPortType.ETH]: { label: 'ETH' },
  [OntPortType.ETHBUNDLE]: { label: 'ETHBUNDLE' },
  [OntPortType.VDSL2]: { label: 'VDSL2' },
  [OntPortType.POTS]: { label: 'POTS' },
  [OntPortType.WIFI]: { label: 'WIFI' },
  _default: { label: '-' },
});

export enum IgmpVersion {
  V2 = 'V2',
  V3 = 'V3',
  V3FORCED = 'V3FORCED',
  V2WITHQUERY = 'V2WITHQUERY',
}

export enum GlobalLeave {
  ON = 'ON',
  OFF = 'OFF',
}

export enum BuildTopo {
  TRUE = 'TRUE',
  FALSE = 'FALSE',
}

export enum MacLearn {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
}

export const igmpVersionOptions = createEnumOptions({
  [IgmpVersion.V2]: {
    label: 'V2',
  },
  [IgmpVersion.V3]: {
    label: 'V3',
  },
  [IgmpVersion.V3FORCED]: {
    label: 'V3FORCED',
  },
  [IgmpVersion.V2WITHQUERY]: {
    label: 'V2WITHQUERY',
  },
  _default: {
    label: '-',
  },
});

export const globalLeaveOptions = createEnumOptions({
  [GlobalLeave.ON]: {
    label: 'ON',
  },
  [GlobalLeave.OFF]: {
    label: 'OFF',
  },
  _default: {
    label: '-',
  },
});

export const buildTopoOptions = createEnumOptions({
  [BuildTopo.TRUE]: {
    label: 'TRUE',
  },
  [BuildTopo.FALSE]: {
    label: 'FALSE',
  },
  _default: {
    label: '-',
  },
});

export const macLearnOptions = createEnumOptions({
  [MacLearn.ENABLE]: {
    label: 'ENABLE',
  },
  [MacLearn.DISABLE]: {
    label: 'DISABLE',
  },
  _default: {
    label: '-',
  },
});

export interface ServicePort {
  portType: ServicePortType | null;
  vlanid?: number; //Network-side outer VLAN ID. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5])$.
  tx?: string; //Name of the upstream traffic profile referenced by the Tx end. The value must meet the following regular expression: ^[^\\f\\r\\n\\r\\,\\:\\;]{1,32}$.
  gemportid: number; //User-side VLAN ID. The value must meet the following regular expression: ^(?:[0-9]?[\\d]|1[0-1][\\d]|12[0-7]|12[8-9]|1[3-9][\\d]|[1-3][\\d][\\d][\\d]|[2-9][\\d][\\d])$.
  rx?: string; //Name of the downstream traffic profile referenced by the Rx end. The value must meet the following regular expression: ^[^\\f\\r\\n\\r\\,\\:\\;]{1,32}$.
  uv?: number | 'any' | 'untagged' | 'priority-tagged'; //User-side VLAN ID. The value must meet the following regular expression: ^(?:[0-3]?[\\d]{1,3}|40[0-8][\\d]|409[0-5]|any|untagged|priority-tagged)$.
  innervlanid?: number; //Network-side inner VLAN ID. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5])$.
  circuitId?: string; //rid Description of the service port, which cannot contain spaces, tab characters, form-feed characters, commas (,), semicolons (;), double quotation marks ("), or question marks (?). The value must meet the following regular expression: ^[^\\f\\r\\n\\r\\,\\;]{1,256}$.
  maclearnnum?: string; //This field may contain sensitive or personal data. Keep it secure. The value must meet the following regular expression: ^(?:[0-9]|[1-9][0-9]|[1-9][\\d]{2}||10[0-1][0-9]|102[0-3])$.
  macbindnum?: string; //This field may contain sensitive or personal data. Keep it secure. The value must meet the following regular expression: ^(?:[0-8])$.
  tagtransform?: ServicePortTagTransform; //Whether to enable transparent VLAN transmission. The value must meet the following regular expression: ^(?:[0-6])$.
  innervlanpri?: number; //Priority of upstream inner VLANs. The value must meet the following regular expression: ^[0-7]$.

  // aoe: string; //If you set this parameter, the AoE service port is created. The value must meet the following regular expression: ^(?:true|TRUE)$.
  // transparent: string; //If you set this parameter, the transparent transmission service port is created. The value must meet the following regular expression: ^(?:true|TRUE)$.
  // svpid: string; //Name of the service port, which cannot contain tab characters, form-feed characters, carriage return characters, line-feed characters, colons (:), commas (,), semicolons (;), or question marks (?). The value must meet the following regular expression: ^[^\\f\\r\\n\\t\\,\\;\\?]{1,253}$.
  // avlanid: string; //User-side outer VLAN ID. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5])$.
  // vpi: string; //Virtual path identifier. Except for GPON and EPON, you need to set a VPI and VCI. If you do not set a VPI or VCI, a VPI or VCI is automatically allocated based on the device. The value must meet the following regular expression: ^(?:[0-3]?[\\d]{1,3}|40[0-8][\\d]|409[0-5]|auto)$.
  // vci: string; //Virtual channel identifier. Except for GPON and EPON, you need to set a VPI and VCI. If you do not set a VPI or VCI, a VPI or VCI is automatically allocated based on the device. The value must meet the following regular expression: ^(?:3[2-9]|[4-9][\\d]|[\\d]{3,4}|[1-5][\\d]{4}|6[0-4][\\d]{3}|65[0-4][\\d]{2}|655[0-2][\\d]|6553[0-5]|auto)$.
  // mduip: string; //Network-side inner VLAN ID. The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\;]{1,64})$.
  // ontinterfacetype: string; //ONT interface type. The value must meet the following regular expression: ^(?:ETH|IPHOST|ONT|VDSL2)$.
  // ontinterfaceid: string; //List of ONT interfaces. The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\:\\;]{1,32})$.
  // cnuid: string; //CUN ID The value must meet the following regular expression: ^(?:[0-9]?[\\d]|1[0-1][0-9]|12[0-7])$.
  // cnukey: string; //This field may contain sensitive or personal data. Keep it secure. The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\:\\;]{1,16})$.
  // cnuuniports: string; //List of UNI IDs on the CNU. The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\:\\;]{1,32})$.
  // cnuuniwifi: string; //Wi-Fi port of a CNU user. The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\:\\;]{1,32})$.
  // channelid: string; //Channel ID. This parameter is applicable to UA5000(IPMB). Set this parameter when the port type is VDSL2. The value must meet the following regular expression: ^[1-2]$.
  // supc: string; //Src The value must meet the following regular expression: ^[1-2]$.
  // tupc: string; //Dst The value must meet the following regular expression: ^[1-2]$.
  // suv: string; //Start user-side VLAN. Set this parameter when you create an EPON other-all service port. The value must meet the following regular expression: ^(?:[0-3]?[\\d]{1,3}|40[0-8][\\d]|409[0-5])$.
  // euv: string; //End user-side VLAN. Set this parameter when you create an EPON other-all service port. The value must meet the following regular expression: ^(?:[0-3]?[\\d]{1,3}|40[0-8][\\d]|409[0-5])$.
  // up: string; //802 The value must meet the following regular expression: ^(?:[^\\f\\r\\n\\r\\,\\:\\;]{1,32})$.
  // ue: string; //Encapsulation mode of user-side packets. The value must meet the following regular expression: ^[0-3]$.
  // uinnervlanid: string; //User-side inner VLAN ID. Set this parameter when a double-tagged user-side VLAN is used. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5])$.
  portmode?: PortMode; //Port mode. It is used as locating information. Only the service port of the LAN-VDSL2 or LAN-G.SHDSL type supports this parameter. The value must meet the following regular expression: ^(?:ptm|atm)$.
  // sfn: string; //Network-side chassis number at the source end. It is used for location. Set this parameter when creating the connection-oriented service port. The value must meet the following regular expression: ^(?:[0-1]?[\\d]{1,2}|2[0-4][\\d]|25[0-5])$.
  // ssn: string; //Network-side slot number at the source end. It is used for location. Set this parameter when creating the connection-oriented service port. The value must meet the following regular expression: ^(?:[0-2]?[\\d]|3[0-5])$.
  // spn: string; //Network-side port number at the source end. It is used for location. Set this parameter when creating the connection-oriented service port. The value must meet the following regular expression: ^(?:[0-5]?[\\d]|6[0-3])$.
  // soutervlanid: string; //Network-side outer VLAN ID of the source end. It is used for location. Set this parameter when creating the connection-oriented service port. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5]|untagged)$.
  // sinnervlanid: string; //Network-side inner VLAN ID of the source end. It is used for location. Set this parameter when creating the connection-oriented service port. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{1,2}|[1-3][\\d]{3}|40[0-8][\\d]|409[0-5])$.
  // cosvalue: string; //Priority of adding the service port in bundle. The value must meet the following regular expression: ^(?:(?:[0-7]|6553[7-9]|655[4-9]\\d|656[\\d]{2}|657[0-8]\\d|6579[0-1]) |(?:(?:(?:(?:(?:(?:[0-7])-{1})(?:[0-7]))){1}|[0-7])(?:~(?:(?:(?:(?:[0-7])-{1})(?:[0-7]))|[0-7])){0,}))$.
  // pvcbundle: string; //Whether to enable the PVC bundle for the service port. The service port that is of the Ethernet, VDSL2 PTM, or GPON type and the auto-sensing service port do not support this parameter. The value must meet the following regular expression: ^(?:0|1)$.
  // bundleid: string; //ID of adding a service port in bundle. The value must meet the following regular expression: ^(?:[1-9]|[1-9][\\d]{0,3}|[1-2][\\d]{4}|[3][0-1][\\d]{3}|[3][2][0-6][\\d]{2}|[3][2][7][0-5][\\d]|[3][2][7][6][0-8])$.
  // encaptype: string; //Encapsulation type of the service port. (When this parameter is set to auto, you do not need to set SIP.) The value must meet the following regular expression: ^(?:[1-7]|auto|llcBridge|llcPPP|llcIP|vcmuxBridge|vcmuxPPP|vcmuxIP)$.
  // sip: string; //Source IP address. The value must meet the following regular expression: (((25[0-5]|2[0-4]\\d|[0-1]\\d{2}|[1-9]?\\d)\\.){3}(25[0-5]|2[0-4]\\d|[0-1]\\d{2}|[1-9]?\\d))|((?i)((([\\da-f]{1,4}:){7}[\\da-f]{1,4})|(([\\da-f]{1,4}:){1,7}:)|(([\\da-f]{1,4}:){6}:[\\da-f]{1,4})|(([\\da-f]{1,4}:){5}(:[\\da-f]{1,4}){1,2})|(([\\da-f]{1,4}:){4}(:[\\da-f]{1,4}){1,3})|(([\\da-f]{1,4}:){3}(:[\\da-f]{1,4}){1,4})|(([\\da-f]{1,4}:){2}(:[\\da-f]{1,4}){1,5})|([\\da-f]{1,4}:(:[\\da-f]{1,4}){1,6})|(:(:[\\da-f]{1,4}){1,7})|(([\\da-f]{1,4}:){6}(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){5}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){4}(:[\\da-f]{1,4})?:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){3}(:[\\da-f]{1,4}){0,2}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){2}(:[\\da-f]{1,4}){0,3}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|([\\da-f]{1,4}:(:[\\da-f]{1,4}){0,4}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(:(:[\\da-f]{1,4}){0,5}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(::))).
  // tip: string; //Destination IP address. The value must meet the following regular expression: (((25[0-5]|2[0-4]\\d|[0-1]\\d{2}|[1-9]?\\d)\\.){3}(25[0-5]|2[0-4]\\d|[0-1]\\d{2}|[1-9]?\\d))|((?i)((([\\da-f]{1,4}:){7}[\\da-f]{1,4})|(([\\da-f]{1,4}:){1,7}:)|(([\\da-f]{1,4}:){6}:[\\da-f]{1,4})|(([\\da-f]{1,4}:){5}(:[\\da-f]{1,4}){1,2})|(([\\da-f]{1,4}:){4}(:[\\da-f]{1,4}){1,3})|(([\\da-f]{1,4}:){3}(:[\\da-f]{1,4}){1,4})|(([\\da-f]{1,4}:){2}(:[\\da-f]{1,4}){1,5})|([\\da-f]{1,4}:(:[\\da-f]{1,4}){1,6})|(:(:[\\da-f]{1,4}){1,7})|(([\\da-f]{1,4}:){6}(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){5}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){4}(:[\\da-f]{1,4})?:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){3}(:[\\da-f]{1,4}){0,2}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(([\\da-f]{1,4}:){2}(:[\\da-f]{1,4}){0,3}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|([\\da-f]{1,4}:(:[\\da-f]{1,4}){0,4}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(:(:[\\da-f]{1,4}){0,5}:(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])(\\.(\\d|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])){3})|(::))).
  unkownmultipolicy?: UnknownMulticastPolicy; //Policy for suppressing unknown multicast traffic. The value must meet the following regular expression: ^(?:discard|transparent)$.
  igmppacketpolicy?: IgmpPacketPolicy; //IGMP packet forwarding mode. The value must meet the following regular expression: ^(?:discard|transparent|normal)$.
  option82sw?: EnableDisable; //Whether to enable the DHCP option 82 function. The value must meet the following regular expression: ^(?:Enable|Disable)$.
  fbuseroption82?: EnableDisable; //Whether to enable the user-side DHCP packet to carry the option 82 information The value must meet the following regular expression: ^(?:Enable|Disable)$.
  antiipspoofing?: EnableDisable; //Whether to enable anti-IP spoofing. The value must meet the following regular expression: ^(?:Enable|Disable)$.
  antiipv6spoofing?: EnableDisable; //Whether to enable anti-IPv6 spoofing. The value must meet the following regular expression: ^(?:Enable|Disable)$.
  pitpfunc?: EnableDisable; //Whether to enable PITP. The value must meet the following regular expression: ^(?:Enable|Disable)$.
  pitpforwarding?: EnableDisable; //Whether to enable PITP forwarding. The value must meet the following regular expression: ^(?:Enable|Disable)$.
  // bundlename: string; //Name of adding a service port in bundle. The value must meet the following regular expression: ^(?:[^\\,\\#\\;\\?]{1,64})$.
  // dnetid: number; //dnetid applies only to MA58 series NEs. This parameter is optional and its default value is 0. [0-8]
  tcontid?: string; // 0-127 used for dba profiles binding
}
